'use client';

import { ArrowLeftXs } from '@lichtblick/photon/icons/arrows/arrow-left-xs';
import { ArrowRightXs } from '@lichtblick/photon/icons/arrows/arrow-right-xs';
import classNames from 'classnames';
import {
  Children,
  type FC,
  isValidElement,
  type MouseEventHandler,
  type PropsWithChildren,
  type ReactElement,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import type { SliderSectionType } from '../../types/storyblok';
import styles from './slider-section.module.scss';

interface Props {
  id: string;
}

type SlideElement = ReactElement<SliderSectionType['items'][number]>;

const scrollSlideIntoView: MouseEventHandler<HTMLAnchorElement> = (e) => {
  e.preventDefault();

  const target = document.querySelector(e.currentTarget.hash);

  if (target) {
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }
};

const SliderIndicators: FC<
  Props & {
    sliderRef: RefObject<HTMLUListElement | null>;
    slides: SlideElement[];
  }
> = ({ id, sliderRef, slides }) => {
  const [lowerActiveIndex, setLowerActiveIndex] = useState<number>(Number.MIN_SAFE_INTEGER);
  const [upperActiveIndex, setUpperActiveIndex] = useState<number>(Number.MAX_SAFE_INTEGER);

  useEffect(() => {
    if (!sliderRef.current) {
      return;
    }

    const slider = sliderRef.current;
    const ratios = new Map<Element, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          ratios.set(entry.target, entry.intersectionRatio);
        }

        const currentRatios = Array.from(ratios.values());

        setLowerActiveIndex(currentRatios.findIndex((ratio) => ratio > 0.9));
        setUpperActiveIndex(currentRatios.findLastIndex((ratio) => ratio > 0.9));
      },
      {
        threshold: Array.from({ length: 11 }, (_, i) => i / 10),
        root: slider,
      },
    );

    for (const slide of slider.children) {
      observer.observe(slide);
    }

    return () => {
      observer.disconnect();
    };
  }, [sliderRef]);

  return (
    <nav className={styles.nav}>
      {lowerActiveIndex !== Number.MIN_SAFE_INTEGER && (
        <a
          className={classNames(styles.arrow, lowerActiveIndex === 0 && styles.disabled)}
          href={`#${id}-${lowerActiveIndex - 1}`}
          onClick={scrollSlideIntoView}
          tabIndex={-1}
        >
          <ArrowLeftXs />
        </a>
      )}
      {slides.map((slide, index) => (
        // biome-ignore lint/a11y/useAnchorContent: just a jump mark for slider w/o js
        <a
          className={classNames(
            styles.bullet,

            index >= lowerActiveIndex && index <= upperActiveIndex && styles.active,
          )}
          href={`#${id}-${index}`}
          key={slide.key}
          onClick={scrollSlideIntoView}
          tabIndex={-1}
          title={slide.props.component === 'editorial-slide' ? slide.props.headline : (slide.props.image.alt ?? '')}
        />
      ))}
      {upperActiveIndex !== Number.MAX_SAFE_INTEGER && (
        <a
          className={classNames(styles.arrow, upperActiveIndex === slides.length - 1 && styles.disabled)}
          href={`#${id}-${upperActiveIndex + 1}`}
          onClick={scrollSlideIntoView}
          tabIndex={-1}
        >
          <ArrowRightXs />
        </a>
      )}
    </nav>
  );
};

export const Slider: FC<PropsWithChildren<Props>> = ({ children, id }) => {
  const slides = Children.toArray(children).filter((child): child is SlideElement => isValidElement(child));
  const sliderRef = useRef<HTMLUListElement>(null);

  return (
    <>
      <ul className={styles.slides} ref={sliderRef}>
        {children}
      </ul>
      <SliderIndicators id={id} sliderRef={sliderRef} slides={slides} />
    </>
  );
};
